import { ListItemIcon, ListItemText, MenuItem } from '@mui/material'
import { useState } from 'react'
import { ConfirmDialog } from '@omnea-app/frontend/components/layout/dialog/ConfirmDialog'
import { LockedPadlockOMIcon, UnlockedPadlockOMIcon } from '@omnea-app/icons'
import { isPermitted } from './isPermitted'
import { noRole } from './roleAccess'
import { useRecordAccess } from './useRecordAccess'
import { useUpdateRecordConfidentiality } from './useUpdateRecordConfidentiality'
import type { PermissionSetEntity, RecordPermissionsFor } from '@omnea-app/authz'
import type { QueryKey } from '@tanstack/react-query'

type ConfidentialityKey =
  | 'confirm-title'
  | 'confirm-description'
  | 'confirm-self-lockout-warning'
  | 'confirm-mark'
  | 'confirm-cancel'
  | 'unmark-confirm-title'
  | 'unmark-confirm-description'
  | 'unmark-confirm-remove'
  | 'update-failed'

type Props<Entity extends PermissionSetEntity> = {
  entity: Entity
  recordId: string
  isConfidential: boolean
  permission: keyof RecordPermissionsFor<Entity>
  invalidateKeys: QueryKey[]
  translateCopy: (key: ConfidentialityKey) => string
  labels: { mark: string; unmark: string }
  /** Called when the confirmation closes, never on click: a menu that unmounts its items on close would take the open dialog with it. */
  onClose?: () => void
}

export const ConfidentialRecordMenuItem = <Entity extends PermissionSetEntity>({
  entity,
  recordId,
  isConfidential,
  permission,
  invalidateKeys,
  translateCopy: t,
  labels,
  onClose,
}: Props<Entity>) => {
  const access = useRecordAccess(entity, recordId)
  const { mutate, isPending } = useUpdateRecordConfidentiality({
    entity,
    recordId,
    invalidateKeys,
    errorMessage: t('update-failed'),
  })
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  // Confidentiality exists only on the permissions service, so no role rule admits the item.
  if (!isPermitted(access, permission, noRole)) return null

  const closeDialog = () => {
    setIsDialogOpen(false)
    onClose?.()
  }

  return (
    <>
      {/* Disabled while the flip is in flight, so reopening the menu against the stale flag cannot queue a second submit. */}
      <MenuItem onClick={() => setIsDialogOpen(true)} disabled={isPending} data-testid={`confidential-${entity}-menu-item`}>
        <ListItemIcon>{isConfidential ? <UnlockedPadlockOMIcon /> : <LockedPadlockOMIcon />}</ListItemIcon>
        <ListItemText>{isConfidential ? labels.unmark : labels.mark}</ListItemText>
      </MenuItem>
      <ConfirmDialog
        open={isDialogOpen}
        onClose={closeDialog}
        onConfirm={() => mutate(!isConfidential)}
        title={isConfidential ? t('unmark-confirm-title') : t('confirm-title')}
        bodyTexts={
          isConfidential ? [t('unmark-confirm-description')] : [t('confirm-description'), t('confirm-self-lockout-warning')]
        }
        actions={[
          { variant: 'contained', color: 'secondary', text: t('confirm-cancel'), action: 'cancel' },
          {
            variant: 'contained',
            color: 'error',
            text: isConfidential ? t('unmark-confirm-remove') : t('confirm-mark'),
            action: 'confirm',
          },
        ]}
      />
    </>
  )
}
